import React, { useRef } from 'react';
import { CheckCircle } from 'lucide-react';
import { haptic } from '../../utils/haptics';

/**
 * Kanonische Tab-Leiste des Design-Systems (Phase 4 — Layout & Dichte).
 *
 * Ersetzt 10+ handgebaute Leisten, von denen mehrere auf 375px abgeschnitten haben.
 *
 * Kernregel: **Nichts wird je abgeschnitten.** Statt horizontal zu scrollen (was
 * Tabs unsichtbar macht) schrumpft bzw. umbricht die Leiste:
 *  - variant="underline" — schrumpft: kleinere Paddings/Schrift auf Mobile, Icons
 *    erst ab sm:, optional `shortLabel` fürs Telefon.
 *  - variant="pill"      — bricht um (flex-wrap) statt zu scrollen.
 *
 * Farben kommen ausschließlich aus Tokens. Damit entfallen die bisherigen
 * dynamisch zusammengesetzten Klassennamen (`bg-${tab.color}-500`), die nur
 * zufällig funktionierten, solange dieselbe Farbe anderswo als Literal vorkam.
 *
 * items: [{ key, label, shortLabel?, icon?, count?, done? }]
 *
 * `done` (Oktober 2026) setzt ein Häkchen „erledigt“ an den Reiter — für
 * Schrittleisten wie die Einrichtung. Der Baustein ordnet es selbst an: neben
 * dem Label, wenn Platz ist, in der schmalen `fill`-Leiste darunter. `done:
 * false` hält dort den Platz frei, damit alle Labels auf einer Linie stehen.
 *
 * `fill` richtet sich nach der Breite der Leiste, nicht des Bildschirms
 * (Oktober 2026): Die Einrichtung steht ab Tablet neben der Seitenleiste und
 * hatte dort weniger Platz als auf dem iPhone, bekam aber ab `sm:` die großen
 * Reiter mit Langtitel und Icon — die Labels liefen ineinander. Darum ist die
 * `fill`-Leiste ein Container (`container-type: inline-size`) und schaltet
 * Schriftgröße, Polster, Kurzlabel und Icon über Container-Abfragen um.
 *
 * `icon` gibt es technisch weiter, aber ein Reiter ist Text ODER Icon, nie
 * gemischt (Oktober 2026): Symbol und Wort nebeneinander sagen dasselbe zweimal.
 *
 * Haptik (Oktober 2026): Ein Wechsel löst `haptic.selection()` aus, und zwar
 * hier im Baustein — Aufrufer ergänzen keine eigene Haptik. Ein Tipp auf den
 * schon aktiven Reiter bleibt stumm.
 */

// Optional verkettet, weil Tests `utils/haptics` oft nur teilweise mocken.
const tick = () => haptic?.selection?.();

const isActiveKey = (itemKey, value) => itemKey === value;

// Ohne `fill`: Umschalten nach Bildschirmbreite, wie bisher.
const VIEWPORT = {
  underline: 'px-2 text-xs sm:px-4 sm:text-sm',
  pill: 'px-3.5 py-2 text-xs sm:px-4 sm:text-sm',
  icon: 'hidden sm:inline',
  short: 'sm:hidden',
  long: 'hidden sm:inline',
  done: 'gap-1',
  pending: 'hidden',
};

// Mit `fill`: Umschalten nach Breite der Leiste. Die Schwelle hängt an der Zahl
// der Reiter — drei passen ab 23rem groß nebeneinander (die Glocke ist 24rem
// breit), vier und mehr erst ab 32rem. Unterstrich-Reiter brauchen dabei nur
// `px-3`: Sie wachsen ohnehin auf gleiche Breite, das Polster ist nur der
// Mindestabstand zwischen zwei Labels. Klassen ausgeschrieben, weil der Tailwind-JIT keine zusammengesetzten
// Namen findet (TOKENS.md, Layout-Regel 2).
const CONTAINER = {
  few: {
    underline: 'px-2 text-xs [@container(min-width:23rem)]:px-3 [@container(min-width:23rem)]:text-sm',
    pill: 'px-3.5 py-2 text-xs [@container(min-width:23rem)]:px-4 [@container(min-width:23rem)]:text-sm',
    icon: 'hidden [@container(min-width:23rem)]:inline',
    short: '[@container(min-width:23rem)]:hidden',
    long: 'hidden [@container(min-width:23rem)]:inline',
    done: 'flex-col gap-0.5 [@container(min-width:23rem)]:flex-row [@container(min-width:23rem)]:gap-1',
    pending: 'invisible [@container(min-width:23rem)]:hidden',
  },
  many: {
    underline: 'px-2 text-xs [@container(min-width:32rem)]:px-3 [@container(min-width:32rem)]:text-sm',
    pill: 'px-3.5 py-2 text-xs [@container(min-width:32rem)]:px-4 [@container(min-width:32rem)]:text-sm',
    icon: 'hidden [@container(min-width:32rem)]:inline',
    short: '[@container(min-width:32rem)]:hidden',
    long: 'hidden [@container(min-width:32rem)]:inline',
    done: 'flex-col gap-0.5 [@container(min-width:32rem)]:flex-row [@container(min-width:32rem)]:gap-1',
    pending: 'invisible [@container(min-width:32rem)]:hidden',
  },
};

const Tabs = ({
  items = [],
  value,
  onChange,
  variant = 'underline',
  fill = false,
  className = '',
  ariaLabel = 'Bereiche',
}) => {
  const tabRefs = useRef([]);

  // Pfeiltasten-Navigation gemäß WAI-ARIA Tabs-Pattern.
  const handleKeyDown = (event, index) => {
    const lastIndex = items.length - 1;
    let nextIndex = null;

    if (event.key === 'ArrowRight') nextIndex = index === lastIndex ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') nextIndex = index === 0 ? lastIndex : index - 1;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = lastIndex;

    if (nextIndex === null) return;
    event.preventDefault();
    if (items[nextIndex].key !== value) tick();
    onChange(items[nextIndex].key);
    tabRefs.current[nextIndex]?.focus();
  };

  const isPill = variant === 'pill';

  const sizes = fill ? CONTAINER[items.length > 3 ? 'many' : 'few'] : VIEWPORT;

  // `fill` verteilt die Tabs gleichmaessig ueber die volle Breite — das Muster fuer
  // Leisten direkt unter einem Seitenkopf. Ohne diese Option draengen sich zwei,
  // drei Tabs am linken Rand und der Rest der Zeile bleibt leer.
  //
  // Platzmangel (Oktober 2026): Bis dahin hiess `fill` `flex-1 min-w-0` ohne
  // Umbruch. Die Tabs wurden damit auch dann gleich breit, wenn ein Label nicht
  // hineinpasste, und der Text lief in den Nachbarn (Einrichtung auf 390px:
  // „Nutzer:innen ✓“ lag über „Tour“, der Strich des aktiven Tabs unter fremdem
  // Text). Jetzt ist jeder Tab mindestens so breit wie sein Inhalt; gleich breit
  // werden sie nur, solange Platz ist. Reicht die Zeile trotzdem nicht, bricht
  // die Leiste als letzte Sicherung um — ein Tab in der zweiten Zeile ist
  // sichtbar, ein überlappender nicht.
  const container = fill ? '[container-type:inline-size]' : '';
  const listClasses = isPill
    ? `flex flex-wrap gap-2 ${container} ${className}`
    : `flex flex-wrap border-b border-border ${container} ${className}`;

  return (
    <div role="tablist" aria-label={ariaLabel} className={listClasses}>
      {items.map((item, index) => {
        const active = isActiveKey(item.key, value);
        const Icon = item.icon;

        // Unterstrich-Variante (Oktober 2026): `items-stretch`, damit der
        // Inhalt die volle Knopfhöhe einnimmt — der Strich unter ihm sitzt
        // dann genau auf der Grundlinie der Leiste.
        const base =
          `inline-flex ${isPill ? 'items-center' : 'items-stretch'} justify-center gap-1.5 min-h-[44px] font-medium ` +
          'transition-colors duration-base ease-standard touch-manipulation ' +
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary';

        // `grow basis-0` = gleich breit, `min-w-fit` = nie schmaler als der
        // Inhalt, `whitespace-nowrap` = kein Umbruch mitten im Label.
        const fillClass = fill ? 'grow basis-0 min-w-fit whitespace-nowrap' : '';

        const sizing = isPill
          ? `${sizes.pill} rounded-full ${fillClass}`
          : `${sizes.underline} ${fillClass}`;

        // Aktiv = gefuellte Pille in Tinte (Okt 2026, „App wie Spot“). Im
        // August war die Fuellung lila und deshalb ein zweiter lauter Farbblock
        // neben den Primaerknoepfen — die Loesung damals war eine helle Flaeche
        // mit lila Schrift. Der Werbespot zeigt die dritte Moeglichkeit: Tinte.
        // Sie ist so eindeutig wie die alte Fuellung und verbraucht kein Lila
        // (CorporateDesign: hoechstens drei lila Elemente pro Bildschirm).
        //
        // Unterstrich (Oktober 2026): Der Strich ist kein Rahmen des Knopfes
        // mehr, sondern ein eigener Strich unter dem Inhalt (siehe unten). Als
        // `border-b-2` lief er über die volle Knopfbreite samt Polster, in
        // einer `fill`-Leiste also bis an den Nachbarn heran; jetzt ist er so
        // breit wie das Label.
        const tone = isPill
          ? active
            ? 'bg-foreground text-background border border-foreground font-semibold'
            : 'bg-surface-raised text-muted-foreground border border-border hover:text-foreground'
          : active
            ? 'text-foreground'
            : 'text-muted-foreground hover:text-foreground';

        const content = (
          <>
            {Icon && <Icon className={`${sizes.icon} w-4 h-4 shrink-0`} aria-hidden="true" />}
            <span className={typeof item.done === 'boolean' ? `inline-flex items-center ${sizes.done}` : 'contents'}>
              {/* shortLabel spart auf dem Telefon Platz, ohne den Tab zu verstecken */}
              {item.shortLabel ? (
                <>
                  <span className={sizes.short}>{item.shortLabel}</span>
                  <span className={sizes.long}>{item.label}</span>
                </>
              ) : (
                <span>{item.label}</span>
              )}
              {item.done === true && (
                <CheckCircle className="h-3.5 w-3.5 shrink-0 text-success" aria-label="erledigt" />
              )}
              {item.done === false && (
                <span className={`${sizes.pending} h-3.5 w-3.5 shrink-0`} aria-hidden="true" />
              )}
            </span>
            {/* Der Zaehler bleibt neutral, auch im aktiven Tab: Welcher Tab
                gewaehlt ist, sagt schon die erhabene Flaeche unter ihm. Ein
                zusaetzlich eingefaerbtes Zahlenpuenktchen wiederholt diese
                Information nur — und zwar lauter als die Zahl es verdient. */}
            {typeof item.count === 'number' && item.count > 0 && (
              <span
                className={`ml-1 shrink-0 rounded-full px-1.5 py-0.5 text-2xs font-semibold ${
                  active && isPill
                    ? 'bg-background/20 text-background'
                    : active
                      ? 'bg-foreground/10 text-foreground'
                      : 'bg-foreground/10 text-muted-foreground'
                }`}
              >
                {item.count}
              </span>
            )}
          </>
        );

        return (
          <button
            key={item.key}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${item.key}`}
            aria-selected={active}
            aria-controls={`tabpanel-${item.key}`}
            tabIndex={active ? 0 : -1}
            onClick={() => {
              if (item.key !== value) tick();
              onChange(item.key);
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={`${base} ${sizing} ${tone}`}
          >
            {isPill ? content : (
              <span className="relative inline-flex min-w-0 items-center gap-1.5 py-3">
                {content}
                {active && (
                  <span aria-hidden="true" className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-foreground" />
                )}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;
